import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import {
    PeopleRegular,
    MoneyRegular,
    ArrowTrendingRegular,
    ClockRegular,
    DocumentTextRegular,
} from '@fluentui/react-icons';

import * as CashFlowStatisticsService from '../../../services/CashFlow-Statistics.Services';
import {
    OverviewStatisticViewModel,
    CashflowSeriesPointViewModel,
    DebtSeriesPointViewModel,
    AgingBucketStatisticViewModel,
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

import '../../Contracts/statistics/ContractStatistics.Styles.css';
import './SponsorStatistics.Styles.css';

import { Props, mapStateToProps, mapDispatchToProps, fmt } from './SponsorStatistics.Functions';

// ─────────────────────────────────────────────────────
// Status display maps
// ─────────────────────────────────────────────────────

const CONTRACT_STATUS_LABEL: Record<number, string> = {
    1: 'Activo', 2: 'Inactivo', 3: 'Suspendido',
    4: 'Vencido', 5: 'Cancelado', 6: 'Terminado',
};

const CONTRACT_STATUS_COLOR: Record<number, string> = {
    1: '#107c10', 2: '#8a8886', 3: '#ca5010',
    4: '#d13438', 5: '#a4262c', 6: '#605e5c',
};

const PROTOCOL_PHASE_COLOR: Record<string, string> = {
    'I': '#722ed1', 'II': '#0078d4', 'III': '#107c10', 'IV': '#ca5010',
};

const PROTOCOL_PHASE_DEFAULT = '#8a8886';

// ─────────────────────────────────────────────────────
// KpiCell — icon + value + label + description
// ─────────────────────────────────────────────────────

interface KpiCellProps {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    desc: string;
    sub?: string;
    iconColor?: 'blue' | 'green' | 'orange' | 'gray';
}

const KpiCell: React.FC<KpiCellProps> = ({ icon, value, label, desc, sub, iconColor = 'blue' }) => (
    <div className="cs-kpi-cell">
        <div className={`cs-kpi-cell__icon cs-kpi-cell__icon--${iconColor}`}>{icon}</div>
        <div className="cs-kpi-cell__value">{value}</div>
        <div className="cs-kpi-cell__label">{label}</div>
        <div className="cs-kpi-cell__desc">{desc}</div>
        {sub && <div className="cs-kpi-cell__sub">{sub}</div>}
    </div>
);

// ─────────────────────────────────────────────────────
// SectionCard — white card with title and optional description
// ─────────────────────────────────────────────────────

interface SectionCardProps {
    title: string;
    desc?: string;
    children: React.ReactNode;
    noPadding?: boolean;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, desc, children, noPadding }) => (
    <div className="cs-card">
        <div className="cs-card__header">
            <div className="cs-card__title">{title}</div>
            {desc && <div className="cs-card__desc">{desc}</div>}
        </div>
        <div className={noPadding ? 'cs-card__chart-body' : 'cs-card__body'}>
            {children}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────

const sponsorDisplayName = (sponsor: any): string =>
    sponsor?.person?.businessName ||
    [sponsor?.person?.firstName, sponsor?.person?.lastName].filter(Boolean).join(' ') ||
    `Sponsor #${sponsor?.id ?? ''}`;

const formatDate = (d?: Date | string): string => {
    if (!d) return '—';
    try {
        return new Intl.DateTimeFormat('es-AR', {
            year: 'numeric', month: 'short', day: 'numeric',
        }).format(new Date(d));
    } catch {
        return String(d);
    }
};

// ─────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────

const SponsorStatisticsComponent = (props: Props) => {
    const { SponsorId, Sponsor, Contracts, Protocols, IsLoading, IsLoadingContracts, IsLoadingProtocols, AccessToken } = props;
    const navigate = useNavigate();

    // ── Load data on mount ───────────────────────────────
    useEffect(() => {
        props.GetSponsorById?.(SponsorId);
        props.GetAllContractAssociations?.(SponsorId);
        props.GetAllContracts?.();
        props.GetAllMedicalProtocols?.();
    }, [SponsorId]);

    // ── CashFlow state ───────────────────────────────────
    const [cfOverview, setCfOverview] = useState<OverviewStatisticViewModel[]>([]);
    const [cfSeries, setCfSeries] = useState<CashflowSeriesPointViewModel[]>([]);
    const [cfDebtSeries, setCfDebtSeries] = useState<DebtSeriesPointViewModel[]>([]);
    const [cfAging, setCfAging] = useState<AgingBucketStatisticViewModel[]>([]);
    const [cfLoading, setCfLoading] = useState(false);
    const [cfCurrency, setCfCurrency] = useState<string>('ARS');

    useEffect(() => {
        if (!AccessToken || !SponsorId) return;
        let cancelled = false;
        setCfLoading(true);
        const filter = { sponsorId: SponsorId };
        const seriesFilter = { sponsorId: SponsorId, granularity: 'Month' as const };
        Promise.all([
            CashFlowStatisticsService.GetOverview(AccessToken, filter).catch(() => []),
            CashFlowStatisticsService.GetCashflowSeries(AccessToken, seriesFilter).catch(() => []),
            CashFlowStatisticsService.GetDebtSeries(AccessToken, seriesFilter).catch(() => []),
            CashFlowStatisticsService.GetAging(AccessToken, filter).catch(() => []),
        ]).then(([ov, cs, ds, ag]) => {
            if (cancelled) return;
            setCfOverview(ov as OverviewStatisticViewModel[]);
            setCfSeries(cs as CashflowSeriesPointViewModel[]);
            setCfDebtSeries(ds as DebtSeriesPointViewModel[]);
            setCfAging(ag as AgingBucketStatisticViewModel[]);
            const firstCurrency =
                (ov as OverviewStatisticViewModel[])[0]?.currencyCode ??
                (cs as CashflowSeriesPointViewModel[])[0]?.currencyCode ??
                'ARS';
            setCfCurrency(firstCurrency);
        }).finally(() => { if (!cancelled) setCfLoading(false); });
        return () => { cancelled = true; };
    }, [AccessToken, SponsorId]);

    // ── CashFlow derived values ──────────────────────────
    const cfOverviewForCurrency = useMemo(
        () => cfOverview.find(o => o.currencyCode === cfCurrency) ?? cfOverview[0] ?? null,
        [cfOverview, cfCurrency]
    );

    const cfCurrencies = useMemo(() => {
        const s = new Set<string>();
        cfOverview.forEach(o => s.add(o.currencyCode));
        cfSeries.forEach(p => s.add(p.currencyCode));
        return Array.from(s);
    }, [cfOverview, cfSeries]);

    const cfSeriesForCurrency = useMemo(
        () => [...cfSeries.filter(p => p.currencyCode === cfCurrency)]
            .sort((a, b) => a.periodLabel.localeCompare(b.periodLabel)),
        [cfSeries, cfCurrency]
    );

    const cfDebtSeriesForCurrency = useMemo(
        () => [...cfDebtSeries.filter(p => p.currencyCode === cfCurrency)]
            .sort((a, b) => a.periodLabel.localeCompare(b.periodLabel)),
        [cfDebtSeries, cfCurrency]
    );

    const cfAgingForCurrency = useMemo(
        () => cfAging.filter(a => a.currencyCode === cfCurrency),
        [cfAging, cfCurrency]
    );

    // ── ECharts: contracts by status donut ───────────────
    const contractsStatusOption = useMemo(() => {
        if (Contracts.length === 0) return null;
        const statusMap = new Map<number, number>();
        Contracts.forEach(c => {
            statusMap.set(c.contractStatus, (statusMap.get(c.contractStatus) ?? 0) + 1);
        });
        return {
            tooltip: {
                trigger: 'item',
                formatter: (p: any) =>
                    `<b>${p.name}</b>: ${p.value} contrato${p.value === 1 ? '' : 's'} (${p.percent}%)`,
            },
            series: [{
                type: 'pie', radius: ['38%', '65%'],
                data: Array.from(statusMap.entries()).map(([status, count]) => ({
                    name: CONTRACT_STATUS_LABEL[status] ?? `Estado ${status}`,
                    value: count,
                    itemStyle: { color: CONTRACT_STATUS_COLOR[status] ?? '#8a8886' },
                })),
                label: { fontSize: 11, formatter: '{b}\n{d}%' },
                emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
            }],
        };
    }, [Contracts]);

    // ── ECharts: cashflow income/outcome series ───────────
    const cfSeriesChartOption = useMemo(() => {
        if (cfSeriesForCurrency.length === 0) return null;
        const labels = cfSeriesForCurrency.map(p => p.periodLabel);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'cross' },
                formatter: (params: any[]) => {
                    const idx = params[0]?.dataIndex ?? 0;
                    const p = cfSeriesForCurrency[idx];
                    return `<b>${p.periodLabel}</b><br/>` +
                        `Ingresos: ${fmt(p.income, cfCurrency)}<br/>` +
                        `Egresos: ${fmt(p.outcome, cfCurrency)}<br/>` +
                        `Neto: ${fmt(p.net, cfCurrency)}<br/>` +
                        `Neto acum.: ${fmt(p.cumulativeNet, cfCurrency)}`;
                },
            },
            legend: { data: ['Ingresos', 'Egresos', 'Neto acumulado'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 8, top: 14, bottom: 36, containLabel: true },
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, rotate: 30 } },
            yAxis: [
                { type: 'value', splitLine: { lineStyle: { color: '#f0efee' } }, axisLabel: { fontSize: 10 } },
                { type: 'value', splitLine: { show: false }, axisLabel: { fontSize: 10 } },
            ],
            series: [
                {
                    name: 'Ingresos', type: 'bar', barMaxWidth: 28,
                    itemStyle: { color: '#52c41a' }, data: cfSeriesForCurrency.map(p => p.income),
                },
                {
                    name: 'Egresos', type: 'bar', barMaxWidth: 28,
                    itemStyle: { color: '#ff4d4f' }, data: cfSeriesForCurrency.map(p => p.outcome),
                },
                {
                    name: 'Neto acumulado', type: 'line', yAxisIndex: 1,
                    smooth: true, symbol: 'circle', symbolSize: 5,
                    lineStyle: { color: '#0078d4', width: 2 },
                    itemStyle: { color: '#0078d4' },
                    areaStyle: { color: 'rgba(0,120,212,0.07)' },
                    data: cfSeriesForCurrency.map(p => p.cumulativeNet),
                },
            ],
        };
    }, [cfSeriesForCurrency, cfCurrency]);

    // ── ECharts: debt evolution ───────────────────────────
    const cfDebtChartOption = useMemo(() => {
        if (cfDebtSeriesForCurrency.length === 0) return null;
        const labels = cfDebtSeriesForCurrency.map(p => p.periodLabel);
        return {
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'cross' },
                formatter: (params: any[]) => {
                    const idx = params[0]?.dataIndex ?? 0;
                    const p = cfDebtSeriesForCurrency[idx];
                    return `<b>${p.periodLabel}</b><br/>` +
                        `Nueva deuda: ${fmt(p.newDebtAmount, cfCurrency)}<br/>` +
                        `Pagado: ${fmt(p.paidAmount, cfCurrency)}<br/>` +
                        `Pendiente: ${fmt(p.pendingAmount, cfCurrency)}<br/>` +
                        `Tasa liquidación: ${p.settlementRate.toFixed(1)}%`;
                },
            },
            legend: { data: ['Nueva deuda', 'Pagado', 'Pendiente acum.'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 8, right: 8, top: 14, bottom: 36, containLabel: true },
            xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, rotate: 30 } },
            yAxis: [
                { type: 'value', splitLine: { lineStyle: { color: '#f0efee' } }, axisLabel: { fontSize: 10 } },
                { type: 'value', splitLine: { show: false }, axisLabel: { fontSize: 10 } },
            ],
            series: [
                {
                    name: 'Nueva deuda', type: 'bar', barMaxWidth: 28,
                    itemStyle: { color: '#fa8c16' }, data: cfDebtSeriesForCurrency.map(p => p.newDebtAmount),
                },
                {
                    name: 'Pagado', type: 'bar', barMaxWidth: 28,
                    itemStyle: { color: '#52c41a' }, data: cfDebtSeriesForCurrency.map(p => p.paidAmount),
                },
                {
                    name: 'Pendiente acum.', type: 'line', yAxisIndex: 1,
                    smooth: true, symbol: 'circle', symbolSize: 5,
                    lineStyle: { color: '#d13438', width: 2 },
                    itemStyle: { color: '#d13438' },
                    areaStyle: { color: 'rgba(209,52,56,0.06)' },
                    data: cfDebtSeriesForCurrency.map(p => p.cumulativePendingAmount),
                },
            ],
        };
    }, [cfDebtSeriesForCurrency, cfCurrency]);

    // ── ECharts: aging donut ──────────────────────────────
    const cfAgingChartOption = useMemo(() => {
        if (cfAgingForCurrency.length === 0) return null;
        const agingColors: Record<string, string> = {
            '0-30': '#52c41a', '31-60': '#fa8c16', '61-90': '#ff7a00', '90+': '#d13438',
        };
        return {
            tooltip: {
                trigger: 'item',
                formatter: (p: any) =>
                    `<b>${p.name}</b><br/>${fmt(p.value, cfCurrency)}<br/>${p.data.debtCount} deudas (${p.percent}%)`,
            },
            series: [{
                type: 'pie', radius: ['38%', '65%'],
                data: cfAgingForCurrency.map(a => ({
                    name: `${a.bucket} días`,
                    value: a.totalPendingAmount,
                    debtCount: a.debtCount,
                    itemStyle: { color: agingColors[a.bucket] ?? '#8a8886' },
                })),
                label: { fontSize: 11, formatter: '{b}\n{d}%' },
                emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
            }],
        };
    }, [cfAgingForCurrency, cfCurrency]);

    // ── Ongoing protocols (statusId === 3) ───────────────
    const ongoingProtocols = useMemo(
        () => Protocols.filter((p: Protocol) => p.statusId === 3),
        [Protocols]
    );

    // ── ECharts: protocols by phase donut ────────────────
    const protocolsPhaseOption = useMemo(() => {
        if (ongoingProtocols.length === 0) return null;
        const byPhase: Record<string, number> = {};
        ongoingProtocols.forEach((p: Protocol) => {
            const key = p.phase?.code ?? p.phase?.title ?? 'Sin fase';
            byPhase[key] = (byPhase[key] ?? 0) + 1;
        });
        return {
            tooltip: { trigger: 'item', formatter: (x: any) => `<b>Fase ${x.name}</b><br/>${x.value} ongoing (${x.percent}%)` },
            series: [{
                type: 'pie', radius: ['38%', '65%'],
                data: Object.entries(byPhase).map(([k, v]) => ({
                    name: k,
                    value: v,
                    itemStyle: { color: PROTOCOL_PHASE_COLOR[k] ?? PROTOCOL_PHASE_DEFAULT },
                })),
                label: { fontSize: 11, formatter: 'F. {b}\n{d}%' },
                emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
            }],
        };
    }, [ongoingProtocols]);

    // ── ECharts: protocols active/inactive donut ──────────
    const protocolsStatusOption = useMemo(() => {
        if (ongoingProtocols.length === 0) return null;
        const byStatus: Record<string, number> = {};
        ongoingProtocols.forEach((p: Protocol) => {
            const key = p.status?.title ?? (p.active ? 'Activo' : 'Inactivo');
            byStatus[key] = (byStatus[key] ?? 0) + 1;
        });
        const entries = Object.entries(byStatus);
        if (entries.length === 0) return null;
        const statusColors: Record<string, string> = {
            'Activo': '#107c10', 'Inactivo': '#8a8886',
        };
        const palette = ['#0078d4', '#107c10', '#ca5010', '#8a8886', '#d13438', '#498205', '#00b294', '#a4262c'];
        return {
            tooltip: { trigger: 'item', formatter: (x: any) => `<b>${x.name}</b><br/>${x.value} ongoing (${x.percent}%)` },
            series: [{
                type: 'pie', radius: ['38%', '65%'],
                data: entries.map(([k, v], i) => ({
                    name: k,
                    value: v,
                    itemStyle: { color: statusColors[k] ?? palette[i % palette.length] },
                })),
                label: { fontSize: 11, formatter: '{b}\n{d}%' },
                emphasis: { label: { fontSize: 13, fontWeight: 'bold' } },
            }],
        };
    }, [ongoingProtocols]);

    // ── Guard renders ────────────────────────────────────
    if (IsLoading && !Sponsor) {
        return (
            <div className="cs-loading">
                <Spinner size={SpinnerSize.medium} />
                <span>Cargando datos del sponsor…</span>
            </div>
        );
    }

    if (!Sponsor) {
        return (
            <div className="cs-empty">
                <DocumentTextRegular style={{ fontSize: 40, color: '#a19f9d' }} />
                <p>No se encontró información para este sponsor.</p>
            </div>
        );
    }

    const totalContracts = Sponsor.contractsAssociated?.length ?? 0;
    const displayName = sponsorDisplayName(Sponsor);

    return (
        <div className="cs-container">

            {/* ── Hero band ── */}
            <div className="cs-hero ss-hero--sponsor">
                <div className="cs-hero__header">
                    <div className="cs-hero__title">Tablero global del sponsor</div>
                    <div className="cs-hero__subtitle">
                        <strong>{displayName}</strong>
                        {Sponsor.person?.personIdentifier
                            ? ` · CUIT / ID: ${Sponsor.person.personIdentifier}`
                            : ''}
                    </div>
                </div>

                <div className="cs-hero__body">
                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Contratos asociados</div>
                        <div className="cs-hero__kpi-value">
                            {IsLoadingContracts || Sponsor.contractsAssociated === undefined
                                ? '…'
                                : totalContracts}
                        </div>
                        <div className="cs-hero__kpi-desc">
                            Total de contratos vinculados a este sponsor
                        </div>
                    </div>

                    <div className="cs-hero__kpi">
                        <div className="cs-hero__kpi-label">Protocolos On Going</div>
                        <div className="cs-hero__kpi-value">
                            {IsLoadingProtocols ? '…' : ongoingProtocols.length}
                        </div>
                        <div className="cs-hero__kpi-desc">
                            Protocolos de investigación en curso
                        </div>
                    </div>

                    {cfLoading && (
                        <div className="cs-hero__kpi">
                            <Spinner size={SpinnerSize.small} />
                            <div className="cs-hero__kpi-desc" style={{ marginTop: 6 }}>
                                Cargando cashflow…
                            </div>
                        </div>
                    )}

                    {!cfLoading && cfOverviewForCurrency && (
                        <>
                            <div className="cs-hero__kpi cs-hero__kpi--liquidado">
                                <div className="cs-hero__kpi-label">Total cobrado</div>
                                <div className="cs-hero__kpi-value">
                                    {fmt(cfOverviewForCurrency.totalPaymentAmount, cfCurrency)}
                                </div>
                                <div className="cs-hero__kpi-desc">Suma de todos los pagos recibidos</div>
                            </div>
                            <div className="cs-hero__kpi cs-hero__kpi--pendiente">
                                <div className="cs-hero__kpi-label">Deuda pendiente</div>
                                <div className="cs-hero__kpi-value">
                                    {fmt(cfOverviewForCurrency.totalDebtPendingAmount, cfCurrency)}
                                </div>
                                <div className="cs-hero__kpi-desc">Monto total aún no cobrado</div>
                            </div>
                            <div className="cs-hero__kpi">
                                <div className="cs-hero__kpi-label">Posición neta</div>
                                <div className="cs-hero__kpi-value">
                                    {fmt(cfOverviewForCurrency.netPosition, cfCurrency)}
                                </div>
                                <div className="cs-hero__kpi-sub">
                                    {cfOverviewForCurrency.netPosition >= 0 ? 'Superávit' : 'Déficit'}
                                </div>
                                <div className="cs-hero__kpi-desc">Cobrado menos pendiente</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="cs-content">

                {/* ── Contratos section ── */}
                <SectionCard
                    title="Contratos del sponsor"
                    desc="Listado de contratos asociados a este sponsor con estado y período de vigencia."
                >
                    {(IsLoadingContracts || Sponsor.contractsAssociated === undefined) && (
                        <div className="cs-loading" style={{ padding: '20px 0' }}>
                            <Spinner size={SpinnerSize.small} />
                            <span>Cargando contratos…</span>
                        </div>
                    )}

                    {!IsLoadingContracts && Sponsor.contractsAssociated !== undefined && totalContracts === 0 && (
                        <div className="cs-empty">
                            <DocumentTextRegular style={{ fontSize: 36, color: '#c8c6c4' }} />
                            <span>Este sponsor no tiene contratos asociados.</span>
                        </div>
                    )}

                    {Contracts.length > 0 && (
                        <div className="cs-two-col">
                            <div>
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>Contrato</th>
                                                <th>Descripción</th>
                                                <th>Estado</th>
                                                <th>Moneda</th>
                                                <th>Desde</th>
                                                <th>Hasta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Contracts.map((c: Contract) => (
                                                <tr key={c.id}
                                                    onClick={() => navigate(`/contracts/${c.id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <strong>{c.contractNumber || `#${c.id}`}</strong>
                                                    </td>
                                                    <td>{c.contractDescription || '—'}</td>
                                                    <td>
                                                        <span style={{
                                                            padding: '1px 8px',
                                                            borderRadius: 10,
                                                            background: `${CONTRACT_STATUS_COLOR[c.contractStatus] ?? '#8a8886'}22`,
                                                            color: CONTRACT_STATUS_COLOR[c.contractStatus] ?? '#8a8886',
                                                            fontWeight: 600,
                                                            fontSize: 11,
                                                        }}>
                                                            {CONTRACT_STATUS_LABEL[c.contractStatus] ?? `Estado ${c.contractStatus}`}
                                                        </span>
                                                    </td>
                                                    <td>{c.contractCurrency || '—'}</td>
                                                    <td>{formatDate(c.contractStartDate)}</td>
                                                    <td>{formatDate(c.contractEndDate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {contractsStatusOption && (
                                <div>
                                    <div className="cs-cf-chart-title">Distribución por estado</div>
                                    <div className="cs-cf-chart-desc">
                                        Cantidad de contratos según su estado actual.
                                    </div>
                                    <ReactECharts
                                        option={contractsStatusOption}
                                        style={{ height: 230 }}
                                        opts={{ renderer: 'svg' }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </SectionCard>

                {/* ── Protocolos section ── */}
                <SectionCard
                    title="Protocolos del sponsor"
                    desc="Protocolos en estado On Going vinculados a este sponsor, agrupados por fase."
                >
                    {IsLoadingProtocols && (
                        <div className="cs-loading" style={{ padding: '20px 0' }}>
                            <Spinner size={SpinnerSize.small} />
                            <span>Cargando protocolos…</span>
                        </div>
                    )}

                    {!IsLoadingProtocols && ongoingProtocols.length === 0 && (
                        <div className="cs-empty">
                            <DocumentTextRegular style={{ fontSize: 36, color: '#c8c6c4' }} />
                            <span>Este sponsor no tiene protocolos On Going.</span>
                        </div>
                    )}

                    {ongoingProtocols.length > 0 && (
                        <>
                            {/* Protocol KPIs */}
                            <div className="cs-kpi-grid" style={{ marginBottom: 18 }}>
                                <KpiCell
                                    icon={<DocumentTextRegular />}
                                    value={Protocols.length}
                                    label="Total protocolos"
                                    desc="Todos los protocolos asociados al sponsor"
                                    iconColor="blue"
                                />
                                <KpiCell
                                    icon={<ArrowTrendingRegular />}
                                    value={ongoingProtocols.length}
                                    label="On Going"
                                    desc="Protocolos actualmente en curso"
                                    iconColor="green"
                                />
                                <KpiCell
                                    icon={<PeopleRegular />}
                                    value={ongoingProtocols.filter((p: Protocol) => p.contractAssociation !== undefined).length}
                                    label="Con contrato"
                                    desc="Protocolos On Going vinculados a un contrato"
                                    iconColor="orange"
                                />
                            </div>

                            <div className="cs-two-col">
                                {/* Protocols table */}
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>Protocolo</th>
                                                <th>Fase</th>
                                                <th>Especialidad</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ongoingProtocols.map((p: Protocol) => (
                                                <tr key={p.id}
                                                    onClick={() => navigate(`/protocolsagreements/${p.id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <strong>{p.protocolNumber || `#${p.id}`}</strong>
                                                        <div style={{ fontSize: 11, color: '#605e5c', marginTop: 2 }}>
                                                            {p.title}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {p.phase?.code ? (
                                                            <span style={{
                                                                padding: '1px 8px',
                                                                borderRadius: 10,
                                                                background: `${PROTOCOL_PHASE_COLOR[p.phase.code] ?? PROTOCOL_PHASE_DEFAULT}22`,
                                                                color: PROTOCOL_PHASE_COLOR[p.phase.code] ?? PROTOCOL_PHASE_DEFAULT,
                                                                fontWeight: 600,
                                                                fontSize: 11,
                                                            }}>
                                                                Fase {p.phase.code}
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                    <td>{p.specialty?.description ?? '—'}</td>
                                                    <td>
                                                        <span style={{
                                                            padding: '1px 8px',
                                                            borderRadius: 10,
                                                            background: p.active ? '#107c1022' : '#8a888622',
                                                            color: p.active ? '#107c10' : '#8a8886',
                                                            fontWeight: 600,
                                                            fontSize: 11,
                                                        }}>
                                                            {p.status?.title ?? (p.active ? 'Activo' : 'Inactivo')}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Charts */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {protocolsStatusOption && (
                                        <div>
                                            <div className="cs-cf-chart-title">Distribución por estado</div>
                                            <ReactECharts
                                                option={protocolsStatusOption}
                                                style={{ height: 180 }}
                                                opts={{ renderer: 'svg' }}
                                            />
                                        </div>
                                    )}
                                    {protocolsPhaseOption && (
                                        <div>
                                            <div className="cs-cf-chart-title">Distribución por fase</div>
                                            <ReactECharts
                                                option={protocolsPhaseOption}
                                                style={{ height: 180 }}
                                                opts={{ renderer: 'svg' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </SectionCard>

                {/* ── CashFlow section ── */}
                <SectionCard
                    title="CashFlow global del sponsor"
                    desc="Flujo de dinero consolidado de todos los contratos y protocolos de este sponsor: ingresos, egresos, evolución de deuda y antigüedad de saldos pendientes."
                >
                    {/* Currency selector */}
                    {cfCurrencies.length > 1 && (
                        <div className="cs-cf-currencies">
                            {cfCurrencies.map(c => (
                                <button
                                    key={c}
                                    className={`cs-cf-currency-btn${c === cfCurrency ? ' cs-cf-currency-btn--active' : ''}`}
                                    onClick={() => setCfCurrency(c)}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    )}

                    {cfLoading && (
                        <div className="cs-loading" style={{ padding: '32px 0' }}>
                            <Spinner size={SpinnerSize.small} />
                            <span>Cargando cashflow…</span>
                        </div>
                    )}

                    {!cfLoading && cfOverview.length === 0 && cfSeries.length === 0 && (
                        <div className="cs-empty">
                            <MoneyRegular style={{ fontSize: 36, color: '#c8c6c4' }} />
                            <span>Sin datos de cashflow registrados para este sponsor.</span>
                            <span style={{ fontSize: 11, color: '#c8c6c4' }}>
                                Es posible que no existan deudas o pagos registrados aún.
                            </span>
                        </div>
                    )}

                    {!cfLoading && (cfOverview.length > 0 || cfSeries.length > 0) && (
                        <>
                            {/* KPIs overview */}
                            {cfOverviewForCurrency && (
                                <div className="cs-kpi-grid ss-kpi-grid--wide" style={{ marginBottom: 18 }}>
                                    <KpiCell
                                        icon={<ArrowTrendingRegular />}
                                        value={fmt(cfOverviewForCurrency.totalPaymentAmount, cfCurrency)}
                                        label="Total cobrado"
                                        desc="Suma de todos los pagos recibidos de este sponsor"
                                        iconColor="green"
                                    />
                                    <KpiCell
                                        icon={<MoneyRegular />}
                                        value={fmt(cfOverviewForCurrency.totalDebtOriginalAmount, cfCurrency)}
                                        label="Total deuda original"
                                        desc="Suma del valor original de todas las deudas generadas"
                                        iconColor="orange"
                                    />
                                    <KpiCell
                                        icon={<ClockRegular />}
                                        value={fmt(cfOverviewForCurrency.totalDebtPendingAmount, cfCurrency)}
                                        label="Deuda pendiente"
                                        desc="Monto total aún no cobrado en todos los contratos"
                                        iconColor="orange"
                                    />
                                    <KpiCell
                                        icon={<DocumentTextRegular />}
                                        value={fmt(cfOverviewForCurrency.netPosition, cfCurrency)}
                                        label="Posición neta"
                                        desc="Pagos cobrados menos deuda pendiente"
                                        iconColor={cfOverviewForCurrency.netPosition >= 0 ? 'green' : 'orange'}
                                        sub={cfOverviewForCurrency.netPosition >= 0 ? 'Superávit' : 'Déficit'}
                                    />
                                    <KpiCell
                                        icon={<PeopleRegular />}
                                        value={`${cfOverviewForCurrency.settledDebtCount} / ${cfOverviewForCurrency.debtCount}`}
                                        label="Deudas liquidadas"
                                        desc="Deudas saldadas sobre el total registrado"
                                        iconColor="blue"
                                        sub={cfOverviewForCurrency.debtCount > 0
                                            ? `${Math.round((cfOverviewForCurrency.settledDebtCount / cfOverviewForCurrency.debtCount) * 100)}% del total`
                                            : undefined}
                                    />
                                    <KpiCell
                                        icon={<ArrowTrendingRegular />}
                                        value={fmt(cfOverviewForCurrency.totalPaymentPendingImputationAmount, cfCurrency)}
                                        label="Pagos sin imputar"
                                        desc="Cobros recibidos aún no imputados a deudas"
                                        iconColor="gray"
                                    />
                                </div>
                            )}

                            {/* Cashflow series chart */}
                            {cfSeriesChartOption !== null && (
                                <div style={{ marginBottom: 16 }}>
                                    <div className="cs-cf-chart-title">
                                        Flujo mensual de caja (ingresos / egresos)
                                    </div>
                                    <div className="cs-cf-chart-desc">
                                        Barras: ingresos (verde) y egresos (rojo) por mes · Línea: neto acumulado.
                                    </div>
                                    <ReactECharts
                                        option={cfSeriesChartOption}
                                        style={{ height: Math.max(240, cfSeriesForCurrency.length * 22) }}
                                        opts={{ renderer: 'svg' }}
                                    />
                                </div>
                            )}

                            {/* Debt series + Aging side by side */}
                            <div className="cs-two-col">
                                {cfDebtChartOption ? (
                                    <div>
                                        <div className="cs-cf-chart-title">Evolución de deuda mensual</div>
                                        <div className="cs-cf-chart-desc">
                                            Nueva deuda vs pagado · Línea: pendiente acumulado.
                                        </div>
                                        <ReactECharts
                                            option={cfDebtChartOption}
                                            style={{ height: 260 }}
                                            opts={{ renderer: 'svg' }}
                                        />
                                    </div>
                                ) : null}

                                {cfAgingChartOption ? (
                                    <div>
                                        <div className="cs-cf-chart-title">
                                            Antigüedad de deuda pendiente
                                        </div>
                                        <div className="cs-cf-chart-desc">
                                            Distribución del saldo pendiente por días de mora.
                                        </div>
                                        <ReactECharts
                                            option={cfAgingChartOption}
                                            style={{ height: 200 }}
                                            opts={{ renderer: 'svg' }}
                                        />
                                        <div className="cs-table-wrap" style={{ marginTop: 8 }}>
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>Bucket</th>
                                                        <th className="cs-table__amount">Pendiente</th>
                                                        <th>Deudas</th>
                                                        <th>%</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cfAgingForCurrency.map(a => {
                                                        const totalPending = cfAgingForCurrency.reduce(
                                                            (s, x) => s + x.totalPendingAmount, 0
                                                        );
                                                        return (
                                                            <tr key={a.bucket}>
                                                                <td><strong>{a.bucket} días</strong></td>
                                                                <td className="cs-table__amount">
                                                                    {fmt(a.totalPendingAmount, cfCurrency)}
                                                                </td>
                                                                <td>{a.debtCount}</td>
                                                                <td>
                                                                    {totalPending > 0
                                                                        ? `${Math.round((a.totalPendingAmount / totalPending) * 100)}%`
                                                                        : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </SectionCard>

            </div>
        </div>
    );
};

export const SponsorStatistics = connect(
    mapStateToProps,
    mapDispatchToProps
)(SponsorStatisticsComponent as any);

export default SponsorStatistics;
